import { useState } from 'react';
import { Plus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card } from '@/components/common/Card';
import { ExpandChevron } from '@/components/common/ExpandChevron';
import type { LogEntry } from '@/types/log-entry';
import { deriveRecentScoutsTally } from '@/utils/recent-scouts-tally';

import { RecentScoutRow } from './RecentScoutRow';

/**
 * RecentScoutsCard — place-led, icon-free Recent Scouts dashboard card (DASH-S1 / #786).
 *
 * Built on the F2 `Card` shell + F3 `ExpandChevron`. Collapsed by default. The
 * full-width header `<button>` owns `aria-expanded`; `ExpandChevron` owns NO ARIA
 * (button-owns-state convention — WeatherAlertsSection precedent). Expand/collapse
 * is a glyph swap (ChevronDown ↔ ChevronUp), NEVER `transform: rotate`
 * (which sticks under `prefers-reduced-motion`).
 *
 * Collapsed: the icon-free tally line. Expanded: a single date-descending
 * interleaved list of `RecentScoutRow`s (no section headers) + a footer
 * ("+ Log a scout" / "View all scouts →"). Empty state (zero entries): verbatim
 * spec copy + a centered "+ Log a scout".
 */

interface RecentScoutsCardProps {
  entries: LogEntry[];
  onEntryClick: (id: string) => void;
  onAddClick: () => void;
  /** waypointId → name lookup; unresolved ids fall to "Unmarked spot" (A5). */
  waypointNames?: Record<string, string>;
}

const MAX_ROWS = 4;
const ALL_SCOUTS_ROUTE = '/scout/log';

const EMPTY_PRIMARY = 'No scouts logged yet';
const EMPTY_SUBLINE = 'Your first sit marks the start of the season.';

function sortDateDescending(entries: LogEntry[]): LogEntry[] {
  return [...entries].sort((a, b) => {
    const dateCmp = b.date.localeCompare(a.date);
    if (dateCmp !== 0) return dateCmp;
    if (a.time && b.time) return b.time.localeCompare(a.time);
    return 0;
  });
}

export function RecentScoutsCard({
  entries,
  onEntryClick,
  onAddClick,
  waypointNames,
}: RecentScoutsCardProps) {
  const [expanded, setExpanded] = useState(false);

  const isEmpty = entries.length === 0;

  // Empty state — same card shell, compact centered text + a single CTA.
  if (isEmpty) {
    return (
      <Card className="p-5" data-testid="recent-scouts-card" data-empty="true">
        <div className="flex flex-col items-center text-center gap-2">
          <p className="text-[var(--text-body-sm)] text-[var(--color-text-secondary)]">
            {EMPTY_PRIMARY}
          </p>
          <p className="text-[var(--text-body-xs)] text-[var(--color-text-muted)]">
            {EMPTY_SUBLINE}
          </p>
          <button
            type="button"
            onClick={onAddClick}
            className="mt-1 flex items-center gap-1 text-[var(--text-body-sm)] font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            data-testid="recent-scouts-add"
          >
            <Plus size={14} aria-hidden />
            Log a scout
          </button>
        </div>
      </Card>
    );
  }

  const tally = deriveRecentScoutsTally(entries);
  const rows = sortDateDescending(entries).slice(0, MAX_ROWS);

  return (
    <Card data-testid="recent-scouts-card" data-empty="false" data-expanded={expanded ? 'true' : 'false'}>
      {/* Header — the full-width toggle button. Owns aria-expanded (ExpandChevron owns none). */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between px-4 py-3.5"
        data-testid="recent-scouts-toggle"
      >
        <span className="field-label">Recent scouts</span>
        <div className="flex items-center gap-2 min-w-0">
          {!expanded && tally.text && (
            <span
              className="text-[var(--text-body-xs)] text-[var(--color-text-placeholder)] tabular-nums truncate"
              data-testid="recent-scouts-tally"
            >
              {tally.text}
            </span>
          )}
          <ExpandChevron expanded={expanded} className="text-[var(--color-text-muted)]" />
        </div>
      </button>

      {/* Expanded body — rows + footer */}
      {expanded && (
        <div data-testid="recent-scouts-body">
          <div className="border-t-[0.5px] border-[var(--color-border)]">
            {rows.map((entry) => (
              <RecentScoutRow
                key={entry.id}
                entry={entry}
                waypointName={entry.waypointId ? waypointNames?.[entry.waypointId] : undefined}
                onClick={onEntryClick}
              />
            ))}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between px-4 py-2 border-t-[0.5px] border-[var(--color-border)]">
            <button
              type="button"
              onClick={onAddClick}
              className="flex items-center gap-1 text-[var(--text-body-sm)] font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] rounded-[var(--radius-md)] px-1.5 py-0.5 transition-colors"
              data-testid="recent-scouts-add"
            >
              <Plus size={14} aria-hidden />
              Log a scout
            </button>
            <Link
              to={ALL_SCOUTS_ROUTE}
              className="flex items-center gap-1 text-[var(--text-body-sm)] font-semibold text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
              data-testid="recent-scouts-view-all"
            >
              View all scouts
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
        </div>
      )}
    </Card>
  );
}
